import Form from "antd/lib/form";
import Input from "antd/lib/input";
import Radio from "antd/lib/radio";
import Row from "antd/lib/row";
import useApi from "api";
import cc from "classcat";
import RetailStatusColumn from "components/Column/RetailStatusColumn";
import RetailDrawer from "components/Drawer/RetailDrawer";
import RetailFormInput from "components/Form/RetailFormInput";
import RetailPageContainer from "components/Layout/RetailPageContainer";
import CampaignDetailsModal from "components/Modal/CampaignDetailsModal";
import RetailSelect from "components/Select/RetailSelect";
import RetailTable from "components/Table/RetailTable";
import RetailTooltip from "components/Tooltip/RetailTooltip";
import RetailText from "components/Typography/RetailText";
import RetailTitle from "components/Typography/RetailTitle";
import { TrackJS } from "trackjs";
import { useCurrency } from "context/CurrencyProvider";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { validateNumber } from "utils/helpers";

import { LinkOutlined } from "@ant-design/icons";

import cm from "./style.module.scss";
import { Checkbox, Tabs } from "antd";
import RetailNotification from "components/Notification";

const AdPlacementPage = () => {
  const { t } = useTranslation();

  const { api, role, baseURL, isLimitedUser } = useApi();

  const { currencySymbol } = useCurrency();

  const [activeKey, setActiveKey] = useState("");

  const [visible, setVisible] = useState<{
    open: boolean;
    mode: "CREATE" | "UPDATE";
  }>({
    open: false,
    mode: "CREATE",
  });

  const [vastTagInfo, setVastTagInfo] = useState<{
    vastTagVisible: boolean;
    vastTag: string;
  }>({
    vastTagVisible: false,
    vastTag: "",
  });

  const [archiveConfirmationInfo, setArchiveConfirmationInfo] = useState<{
    archiveConfirmationVisible: boolean;
    adPlacementRecord: any;
  }>({
    archiveConfirmationVisible: false,
    adPlacementRecord: {},
  });

  const [isArchiveConfirmed, setIsArchiveConfirmed] = useState(false);

  const [placement, setPlacement] = useState<any | null>(null);

  const [isCopied, setIsCopied] = useState(false);

  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  const [formFields, setFormFields] = useState({
    name: "",
    ad_format: "",
    max_ads: "",
    min_ads: "",
    min_bid: "",
  });

  const isPromoteProductsApplicable =
    formFields.ad_format === "DISPLAY_WITH_PRODUCT" ||
    formFields.ad_format === "VIDEO_WITH_PRODUCT";

  const isVideoType =
    formFields.ad_format === "VIDEO" ||
    formFields.ad_format === "VIDEO_WITH_PRODUCT" ||
    formFields.ad_format === "IN_STORE";

  const { options, fetchData, handleScroll } = useInfiniteScroll(
    "ad_placements/sizes",
    formFields.ad_format === "DISPLAY"
  );

  const targetingOptions = [
    {
      label: t("pages.admin.placement.search"),
      value: "IN_SEARCH",
    },
    {
      label: t("pages.admin.placement.category"),
      value: "IN_CATEGORY",
    },
    {
      label: t("pages.admin.placement.homepage"),
      value: "IN_HOME_PAGE",
    },
    {
      label: t("pages.admin.placement.detail"),
      value: "IN_PRODUCT_DETAIL_PAGE",
    },
    {
      label: t("pages.admin.placement.collection"),
      value: "IN_COLLECTION",
    },
  ];

  useEffect(() => {
    if (placement !== null && visible.mode === "UPDATE") {
      const {
        name,
        ad_format,
        max_ads,
        min_ads,
        min_bid,
        recommended_size,
        sizes,
        cross_sell_blocking_type,
        type,
      } = placement;
      setFormFields({
        name,
        ad_format,
        max_ads,
        min_ads: min_ads || "",
        min_bid: min_bid || "",
      });
      form.setFieldsValue({
        name,
        ad_format,
        max_ads,
        min_ads,
        min_bid,
        recommended_size:
          recommended_size !== undefined ? [recommended_size] : [],
        sizes: sizes,
        cross_sell_blocking_type: cross_sell_blocking_type,
        type,
      });
    }
  }, [visible.mode, placement, form]);

  const placementValues = (values: any) => {
    const { recommended_size, sizes, cross_sell_blocking_type, type } = values;
    const { name, ad_format, max_ads, min_ads, min_bid } = formFields;
    return {
      status: visible.mode === "CREATE" ? "ACTIVE" : placement?.status,
      name,
      ad_format,
      max_ads:
        isPromoteProductsApplicable || isVideoType
          ? 1
          : parseInt(max_ads as string),
      min_ads:
        isPromoteProductsApplicable && +min_ads > 1
          ? 1
          : parseInt(min_ads) || 0,
      min_bid: parseFloat(min_bid),
      recommended_size: recommended_size ? recommended_size[0] : null,
      sizes,
      cross_sell_blocking_type,
      type,
    };
  };

  const open = () => setVisible({ open: true, mode: "CREATE" });

  const open_update = (records: any) => {
    setVisible({
      open: true,
      mode: "UPDATE",
    });
    setPlacement(records);
  };

  const switchTargeting = (type: string) => {
    switch (type) {
      case "IN_SEARCH":
        return t("pages.admin.placement.search");
      case "IN_CATEGORY":
        return t("pages.admin.placement.category");
      case "IN_HOME_PAGE":
        return t("pages.admin.placement.homepage");
      case "IN_PRODUCT_DETAIL_PAGE":
        return t("pages.admin.placement.detail");
      case "IN_COLLECTION":
        return t("pages.admin.placement.collection");
      default:
        return "-";
    }
  };

  const renderColumns = (col: string, value: any, records: any) => {
    switch (col) {
      case "status":
        return <RetailStatusColumn records={records} url="ad_placements" />;
      case "ad_format":
        return value ? t(`pages.admin.placement.${value?.toLowerCase()}`) : "-";
      case "type":
        return switchTargeting(value);
      case "recommended_size":
        return !value || value === "" ? "-" : value;
      case "sizes":
        return (
          <Row className={cm.sizes}>
            {value ? (
              value.map((item: string) => (
                <RetailText size="xs" family="poppins">
                  {item}
                </RetailText>
              ))
            ) : (
              <RetailText size="xs" family="poppins">
                -
              </RetailText>
            )}
          </Row>
        );
      default:
        return value ? value : "-";
    }
  };

  const updateValueArchive = async (data: any) => {
    const isBulkArchive = Array.isArray(data);
    if (isBulkArchive) {
      const config = {
        status: "ARCHIVED",
        id: data,
      };
      const response = await api.patch(`ad_placements`, config);
      return response;
    }
    const config = {
      status: data.status === "ARCHIVED" ? "ACTIVE" : "ARCHIVED",
    };
    const response = await api.patch(`ad_placements/${data.id}`, config);
    return response;
  };

  const { mutateAsync: mutateArchive } = useMutation(updateValueArchive);

  const onArchive = async (records: any): Promise<void> => {
    if(activeKey === "ARCHIVED") {
      changeArchiveStatus(records);
      return;
    }
    
    setArchiveConfirmationInfo({
      archiveConfirmationVisible: true,
      adPlacementRecord: records,
    });
  };

  const onThirdOption = (records: any) => {
    setVastTagInfo({
      vastTagVisible: true,
      vastTag: records.vast_tag,
    });
  };

  const resetArchiveConfirmation = () => {
    setIsArchiveConfirmed(false);
    setArchiveConfirmationInfo({
      archiveConfirmationVisible: false,
      adPlacementRecord: {},
    });
  };

  const changeArchiveStatus = async (records?: any) => {
    const mutationRecords = activeKey === "ARCHIVED" 
      ? records 
      : archiveConfirmationInfo.adPlacementRecord;

    try {
      await mutateArchive(mutationRecords);
      queryClient.refetchQueries("table");
      RetailNotification.showNotification(
        "success",
        "",
        t(`components.notification.archived_ad_placement`)
      );
      resetArchiveConfirmation();
    } catch (error) {
      RetailNotification.showNotification(
        "error",
        "",
        t("components.notification.statusError")
      );
      resetArchiveConfirmation();
    }
  };

  const tableConfig = {
    url: "ad_placements",
    isRelation: false,
    activeKey,
    to: open_update,
    onArchive: onArchive,
    onThirdOption: onThirdOption,
    renderColumns,
  };

  const activeTabConfig = {
    ...tableConfig,
    defaultFilter: [{ key: "status", op: "ne", value: "ARCHIVED" }],
  };

  const archivedTabConfig = {
    ...tableConfig,
    defaultFilter: [{ key: "status", op: "eq", value: "ARCHIVED" }],
  };

  const closeShowVastTag = () =>
    setVastTagInfo({ vastTagVisible: false, vastTag: "" });

  const copyVastTag = () => navigator.clipboard.writeText(vastTagInfo.vastTag);

  const handleCopyClick = () => {
    copyVastTag()
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 3500);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const closeArchiveConfirmation = () => resetArchiveConfirmation();

  const crossOptions = [
    {
      label: t("pages.admin.placement.crossProduct"),
      value: "PRODUCT",
    },
    {
      label: t("pages.admin.placement.crossBlock"),
      value: "DONT_BLOCK",
    },
  ];

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormFields((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const onClose = () => {
    form.resetFields();
    setFormFields({
      name: "",
      ad_format: "",
      max_ads: "",
      min_ads: "",
      min_bid: "",
    });
    setVisible({ open: false, mode: "CREATE" });
  };

  const createPlacement = async (values: any) => {
    try {
      await api.post("ad_placements", placementValues(values));
      queryClient.refetchQueries("table");
      fetchData();
    } catch (error) {
      TrackJS.track({
        message: "Ad Placement Creation Error",
        metadata: {
          endpoint: "/ad_placements",
          requestBody: placementValues(values),
          role,
          isLimitedUser: `Is user limited? ${isLimitedUser}`,
          baseURL: baseURL(),
          responseBody: error?.response?.data,
          statusCode: error?.response?.status,
          errorMessage: error?.message,
          severity: "Medium",
        },
      });
      console.error(error);
    } finally {
      onClose();
    }
  };

  const updatePlacement = async (values: any) => {
    const response = await api.patch(
      `ad_placements/${placement?.id}`,
      placementValues(values)
    );
    return response;
  };

  const { mutateAsync } = useMutation(updatePlacement);

  const changePlacement = async (values: any) => {
    try {
      await mutateAsync(values);
      queryClient.refetchQueries("table");
      fetchData();
    } catch (error) {
      TrackJS.track({
        message: "Ad Placement Update Error",
        metadata: {
          endpoint: "/ad_placements",
          requestBody: placementValues(values),
          role,
          isLimitedUser: `Is user limited? ${isLimitedUser}`,
          baseURL: baseURL(),
          responseBody: error?.response?.data,
          statusCode: error?.response?.status,
          errorMessage: error?.message,
          severity: "Medium",
        },
      });
      console.error(error);
    } finally {
      onClose();
    }
  };

  const onFinish = (values: any) =>
    visible.mode === "CREATE"
      ? createPlacement(values)
      : changePlacement(values);

  return (
    <RetailPageContainer>
      <Tabs
        onChange={(activeKey) => setActiveKey(activeKey)}
        className="bordered-container no-margin"
      >
        <Tabs.TabPane tab={t("common.listed")} key="ACTIVE">
          <RetailTable
            placeholder={t("common.search")}
            tableConfig={activeTabConfig}
            button={{
              title: t("pages.admin.placement.add"),
              onClick: open,
            }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t("common.archived")} key="ARCHIVED">
          <RetailTable
            placeholder={t("common.search")}
            tableConfig={archivedTabConfig}
            addBtnVisible={false}
          />
        </Tabs.TabPane>
      </Tabs>
      <RetailDrawer
        title={
          visible.mode === "CREATE"
            ? t("pages.admin.placement.add")
            : t("pages.admin.placement.update")
        }
        visible={visible.open}
        onClose={onClose}
        onOk={form.submit}
      >
        <Form
          autoComplete="off"
          form={form}
          onFinish={onFinish}
          requiredMark={false}
        >
          <RetailTitle level={5} className={cm.title}>
            {t("pages.admin.placement.format")}
          </RetailTitle>
          <RetailText size="xxxs" family="poppins" className={cm.subtext}>
            {t("pages.admin.placement.formatText")}
          </RetailText>

          {/*
           *Placement input
           */}
          <Form.Item
            name="ad_format"
            className={cc(["flex", cm.placement])}
            rules={[
              {
                required: true,
                message: t("pages.admin.placement.formatError"),
              },
            ]}
          >
            <Radio.Group onChange={handleChange} name="ad_format">
              <Radio value="PRODUCT" className="form-radio">
                <RetailTitle level={5} className={cm.title}>
                  {t("pages.admin.placement.product")}
                </RetailTitle>
                <RetailText className={cm.text} size="xxxs">
                  {t("pages.admin.placement.sponsoredText")}
                </RetailText>
              </Radio>
              <Radio value="DISPLAY" className="form-radio">
                <RetailTitle level={5} className={cm.title}>
                  {t("pages.admin.placement.display")}
                </RetailTitle>
                <RetailText className={cm.text} size="xxxs">
                  {t("pages.admin.placement.displayText")}
                </RetailText>
                {formFields.ad_format === "DISPLAY" && (
                  <>
                    <div className={cm.selectContainer}>
                      <h6 className={cm.secondaryTitle}>
                        {t("pages.admin.placement.suggestedTitle")}
                      </h6>
                      <RetailText size="xxxs" className={cm.selectLabel}>
                        {t("pages.admin.placement.suggestedText")}
                      </RetailText>
                      <Form.Item
                        name="recommended_size"
                        rules={[
                          {
                            type: "array",
                            max: 1,
                            message: t("pages.admin.placement.suggestedError"),
                          },
                        ]}
                      >
                        <RetailSelect
                          onClick={(e) => e.preventDefault()}
                          onPopupScroll={handleScroll}
                          options={options?.map((opt: string) => ({
                            label: opt,
                            value: opt,
                          }))}
                          mode="tags"
                          placeholder={t(
                            "pages.admin.placement.suggestedTitle"
                          )}
                          className={cm.tags}
                          listHeight={350}
                        />
                      </Form.Item>
                    </div>

                    <div className={cm.selectContainer}>
                      <h6 className={cm.secondaryTitle}>
                        {t("pages.admin.placement.acceptedTitle")}
                      </h6>
                      <RetailText size="xxxs" className={cm.selectLabel}>
                        {t("pages.admin.placement.acceptedText")}
                      </RetailText>
                      <Form.Item
                        name="sizes"
                        rules={[
                          {
                            type: "array",
                            min: 1,
                            max: 100,
                            required: true,
                            message: t("pages.admin.placement.acceptedError"),
                          },
                        ]}
                      >
                        <RetailSelect
                          onClick={(e) => e.preventDefault()}
                          onPopupScroll={handleScroll}
                          options={options?.map((opt: string) => ({
                            label: opt,
                            value: opt,
                          }))}
                          mode="tags"
                          placeholder={t("pages.admin.placement.acceptedTitle")}
                          className={cm.tags}
                          listHeight={350}
                        />
                      </Form.Item>
                    </div>
                  </>
                )}
              </Radio>
              <Radio value="CROSS_SELL" className="form-radio">
                <RetailTitle level={5} className={cm.title}>
                  {t("pages.admin.placement.cross_sell")}
                </RetailTitle>
                <RetailText className={cm.text} size="xxxs">
                  {t("pages.admin.placement.crossText")}
                </RetailText>
                {formFields.ad_format === "CROSS_SELL" && (
                  <div className={cm.selectContainer}>
                    <h6 className={cm.secondaryTitle}>
                      {t("pages.admin.placement.crossTitle")}
                    </h6>
                    <RetailText size="xxxs" className={cm.selectLabel}>
                      {t("pages.admin.placement.crossSub")}
                    </RetailText>
                    <Form.Item
                      name="cross_sell_blocking_type"
                      rules={[
                        {
                          required: true,
                          message: t("pages.admin.placement.crossError"),
                        },
                      ]}
                    >
                      <RetailSelect
                        options={crossOptions}
                        placeholder={t(
                          "pages.admin.placement.crossPlaceholder"
                        )}
                        onClick={(e) => e.preventDefault()}
                      />
                    </Form.Item>
                  </div>
                )}
              </Radio>
              <Radio value="VIDEO" className="form-radio">
                <RetailTitle level={5} className={cm.title}>
                  {t("pages.admin.placement.video")}
                </RetailTitle>
                <RetailText className={cm.text} size="xxxs">
                  {t("pages.admin.placement.videoText")}
                </RetailText>
              </Radio>

              <Radio value="DISPLAY_WITH_PRODUCT" className="form-radio">
                <RetailTitle level={5} className={cm.title}>
                  {t("pages.admin.placement.displayWithProduct")}
                </RetailTitle>
                <RetailText className={cm.text} size="xxxs">
                  {t("pages.admin.placement.displayText")}
                </RetailText>
                {formFields.ad_format === "DISPLAY_WITH_PRODUCT" && (
                  <>
                    <div className={cm.selectContainer}>
                      <h6 className={cm.secondaryTitle}>
                        {t("pages.admin.placement.suggestedTitle")}
                      </h6>
                      <RetailText size="xxxs" className={cm.selectLabel}>
                        {t("pages.admin.placement.suggestedText")}
                      </RetailText>
                      <Form.Item
                        name="recommended_size"
                        rules={[
                          {
                            type: "array",
                            max: 1,
                            message: t("pages.admin.placement.suggestedError"),
                          },
                        ]}
                      >
                        <RetailSelect
                          onClick={(e) => e.preventDefault()}
                          onPopupScroll={handleScroll}
                          options={options?.map((opt: string) => ({
                            label: opt,
                            value: opt,
                          }))}
                          mode="tags"
                          placeholder={t(
                            "pages.admin.placement.suggestedTitle"
                          )}
                          className={cm.tags}
                          listHeight={350}
                        />
                      </Form.Item>
                    </div>

                    <div className={cm.selectContainer}>
                      <h6 className={cm.secondaryTitle}>
                        {t("pages.admin.placement.acceptedTitle")}
                      </h6>
                      <RetailText size="xxxs" className={cm.selectLabel}>
                        {t("pages.admin.placement.acceptedText")}
                      </RetailText>
                      <Form.Item
                        name="sizes"
                        rules={[
                          {
                            type: "array",
                            min: 1,
                            max: 100,
                            required: true,
                            message: t("pages.admin.placement.acceptedError"),
                          },
                        ]}
                      >
                        <RetailSelect
                          onClick={(e) => e.preventDefault()}
                          onPopupScroll={handleScroll}
                          options={options?.map((opt: string) => ({
                            label: opt,
                            value: opt,
                          }))}
                          mode="tags"
                          placeholder={t("pages.admin.placement.acceptedTitle")}
                          className={cm.tags}
                          listHeight={350}
                        />
                      </Form.Item>
                    </div>
                  </>
                )}
              </Radio>

              <Radio value="VIDEO_WITH_PRODUCT" className="form-radio">
                <RetailTitle level={5} className={cm.title}>
                  {t("pages.admin.placement.videoWithProduct")}
                </RetailTitle>
                <RetailText className={cm.text} size="xxxs">
                  {t("pages.admin.placement.videoText")}
                </RetailText>
              </Radio>
            </Radio.Group>
          </Form.Item>
          <RetailTitle level={5} className={cm.title}>
            {t("pages.admin.placement.adPlacementType")}
          </RetailTitle>
          <RetailText size="xxxs" family="poppins" className={cm.subtext}>
            {t("pages.admin.placement.adPlacementTypeText")}
          </RetailText>

          <Form.Item
            name="type"
            rules={[
              { required: true, message: t("pages.admin.placement.typeError") },
            ]}
          >
            <RetailSelect
              placeholder={t(
                "pages.admin.placement.adPlacementTypePlaceholder"
              )}
              options={targetingOptions}
            />
          </Form.Item>

          <RetailTitle level={5} className={cm.title}>
            {t("pages.admin.placement.settings")}
          </RetailTitle>
          <RetailText size="xxxs" family="poppins" className={cm.subtext}>
            {t("pages.admin.placement.settingsText")}
          </RetailText>

          {/*
           *Name input
           */}
          <RetailFormInput
            isFocused={formFields.name !== ""}
            label={t("pages.admin.placement.name")}
            name="name"
            help={t("pages.admin.placement.nameText")}
            className="floating"
            rules={[{ required: true }]}
          >
            <Input
              className="floating"
              name="name"
              onChange={handleChange}
              value={formFields.name}
            />
          </RetailFormInput>

          {/*
           *Max Ads and Min Ads input
           */}
          {!isVideoType && (
            <>
              <RetailFormInput
                isFocused={formFields.min_ads !== ""}
                label={t("pages.admin.placement.minAds")}
                name="min_ads"
                help={t(
                  `pages.admin.placement.${
                    isPromoteProductsApplicable ? "promoteText" : "minAdsText"
                  }`
                )}
                rules={[
                  {
                    type: "number",
                    validator: (_, value) => {
                      if (
                        (isPromoteProductsApplicable && value > 1) ||
                        !value
                      ) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className="floating"
              >
                <Input
                  className="floating"
                  name="min_ads"
                  onChange={handleChange}
                  value={formFields.min_ads}
                  onKeyDownCapture={(e) => validateNumber(e)}
                />
              </RetailFormInput>
              <RetailFormInput
                isFocused={formFields.max_ads !== ""}
                label={t("pages.admin.placement.maxAds")}
                name="max_ads"
                help={t(
                  `pages.admin.placement.${
                    isPromoteProductsApplicable ? "promoteText" : "maxAdsText"
                  }`
                )}
                className="floating"
                rules={[
                  {
                    required: true,
                    type: "number",
                    validator: (_, value) => {
                      if (
                        (isPromoteProductsApplicable && value > 1) ||
                        !value
                      ) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  className="floating"
                  name="max_ads"
                  onChange={handleChange}
                  value={formFields.max_ads}
                  onKeyDownCapture={(e) => validateNumber(e)}
                />
              </RetailFormInput>
            </>
          )}

          {/*
           *Min Bid input
           */}
          <RetailFormInput
            isFocused={formFields.min_bid !== ""}
            label={t("pages.admin.placement.minBid")}
            name="min_bid"
            help={t("pages.admin.placement.minBidText")}
            className={cc([
              "floating",
              formFields.min_bid ? "" : cm.hidePrefix,
            ])}
          >
            <Input
              prefix={currencySymbol}
              className="number-input floating"
              name="min_bid"
              onChange={handleChange}
              value={formFields.min_bid}
              onKeyDownCapture={(e) => validateNumber(e)}
            />
          </RetailFormInput>
        </Form>
        {/**
         * Video Warning
         */}
        {isVideoType && (
          <div className={cm.warningContainer}>
            <Trans>
              <p className={cm.warningText}>
                {t("pages.admin.placement.videoWarning")}
              </p>
            </Trans>
          </div>
        )}
      </RetailDrawer>
      <CampaignDetailsModal
        type="SHOW_VAST_TAG"
        visible={vastTagInfo.vastTagVisible}
        onOk={closeShowVastTag}
        onCancel={closeShowVastTag}
      >
        <div className={cm.vastTagContainer}>
          <RetailText size="xs" weight="medium" className={cm.vastTag}>
            {vastTagInfo?.vastTag}
          </RetailText>
          <RetailTooltip
            title={
              isCopied
                ? t("pages.admin.integrationInfo.copied")
                : t("pages.admin.integrationInfo.copy")
            }
            trigger="hover"
          >
            <span className={cm.copyTag} onClick={handleCopyClick}>
              <LinkOutlined />
            </span>
          </RetailTooltip>
        </div>
      </CampaignDetailsModal>
      <CampaignDetailsModal
        type="ARCHIVE_CONFIRMATION"
        visible={archiveConfirmationInfo.archiveConfirmationVisible}
        onOk={changeArchiveStatus}
        onCancel={closeArchiveConfirmation}
        disabled={!isArchiveConfirmed}
      >
        <div className={cm.archiveConfirmationContainer}>
          <RetailText
            size="xs"
            weight="medium"
            className={cm.archiveConfirmationText}
          >
            {t("pages.admin.placement.archive_confirmation_text", {
              name: archiveConfirmationInfo.adPlacementRecord?.name,
            })}
          </RetailText>
          <Checkbox
            onChange={(e) => setIsArchiveConfirmed(e.target.checked)}
            value={isArchiveConfirmed}
            className={cm.creativeLanguageItem}
            defaultChecked={false}
          >
            <RetailText
              size="xs"
              weight="medium"
              className={cm.archiveConfirmationText}
            >
              {t("pages.admin.placement.archive_checkbox_text")}
            </RetailText>
          </Checkbox>
        </div>
      </CampaignDetailsModal>
    </RetailPageContainer>
  );
};

export default AdPlacementPage;
